import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { ChecksTab } from '../components/data-review/ChecksTab';
import { CustomerDepositsTab } from '../components/data-review/CustomerDepositsTab';
import { LedgerEntriesTab } from '../components/data-review/LedgerEntriesTab';
import { QBCustomersTab } from '../components/data-review/QBCustomersTab';
import { QBVendorsTab } from '../components/data-review/QBVendorsTab';
import { PdfViewer, BoundingBox } from '../components/data-review/PdfViewer';
import { dataSourcesApi } from '../api';
import { DataSource } from '../types';

type ViewerType = 'pdf' | 'html' | null;

export const DataReview: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [activeTab, setActiveTab] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState<ViewerType>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [boundingBox, setBoundingBox] = useState<BoundingBox | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingHighlightRow = useRef<number | null>(null);

  // Send highlight message to iframe when row is selected
  const highlightDepositRow = useCallback((rowIndex: number | null) => {
    if (rowIndex == null) {
      pendingHighlightRow.current = null;
      return;
    }

    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) {
      pendingHighlightRow.current = rowIndex;
      return;
    }

    frame.contentWindow.postMessage(
      { type: 'highlightDeposit', rowIndex },
      '*'
    );
    pendingHighlightRow.current = null;
  }, []);

  // Handle iframe load - send pending highlight
  const handleIframeLoad = useCallback(() => {
    if (viewerType !== 'html') return;

    const rowToHighlight = pendingHighlightRow.current ?? selectedRowIndex;
    if (rowToHighlight != null) {
      highlightDepositRow(rowToHighlight);
    }
  }, [viewerType, selectedRowIndex, highlightDepositRow]);

  // Highlight row when selection changes and viewer is HTML
  useEffect(() => {
    if (viewerType === 'html' && selectedRowIndex != null) {
      highlightDepositRow(selectedRowIndex);
    }
  }, [viewerType, selectedRowIndex, highlightDepositRow]);

  // Clear file viewer when switching tabs
  const clearPreview = useCallback(() => {
    setPreviewUrl(null);
    setPreviewError(null);
    setPageNumber(1);
    setBoundingBox(null);
    setViewerType(null);
    setSelectedRowIndex(null);
    pendingHighlightRow.current = null;
  }, []);

  const handleTabChange = useCallback((index: number) => {
    setActiveTab(index);
    clearPreview();
  }, [clearPreview]);

  const loadPreview = useCallback(
    async (
      dataSource: DataSource | null,
      page?: number,
      bbox?: BoundingBox | null,
      rowIndex?: number | null
    ) => {
      if (!dataSource || !clientId) {
        setPreviewUrl(null);
        setPreviewError(null);
        setPageNumber(1);
        setBoundingBox(null);
        setViewerType(null);
        setSelectedRowIndex(null);
        return;
      }

      // Determine viewer type based on content and viewer_path
      const contentType = dataSource.content_type?.toLowerCase() || '';
      const isPdf = contentType.includes('pdf');
      const hasViewerPath = !!dataSource.viewer_path;

      // If it has a viewer_path (HTML preview), use HTML viewer
      // Otherwise if it's a PDF, use PDF viewer
      let newViewerType: ViewerType = null;
      if (hasViewerPath) {
        newViewerType = 'html';
      } else if (isPdf || contentType.startsWith('image/')) {
        newViewerType = 'pdf';
      }

      if (!newViewerType) {
        setPreviewUrl(null);
        setPreviewError(`File type not previewable: ${dataSource.original_filename || 'Unknown file'}`);
        setPageNumber(1);
        setBoundingBox(null);
        setViewerType(null);
        setSelectedRowIndex(null);
        return;
      }

      try {
        setIsLoadingPreview(true);
        setPreviewError(null);
        const url = await dataSourcesApi.getPreviewUrl(clientId, dataSource.id);
        setPreviewUrl(url);
        setViewerType(newViewerType);
        setPageNumber(page || 1);
        setBoundingBox(bbox || null);
        setSelectedRowIndex(rowIndex ?? null);

        // Queue highlight for HTML viewer
        if (newViewerType === 'html' && rowIndex != null) {
          pendingHighlightRow.current = rowIndex;
        }
      } catch (error: any) {
        console.error('Failed to load preview:', error);
        setPreviewError('Failed to load file preview');
        setPreviewUrl(null);
        setViewerType(null);
      } finally {
        setIsLoadingPreview(false);
      }
    },
    [clientId]
  );

  const handleCheckSelect = useCallback(
    (
      _check: any,
      dataSource: DataSource | null,
      page?: number,
      bbox?: BoundingBox | null
    ) => {
      loadPreview(dataSource, page, bbox);
    },
    [loadPreview]
  );

  const handleDepositSelect = useCallback(
    (
      deposit: any,
      dataSource: DataSource | null,
      page?: number
    ) => {
      // Pass row_number for HTML viewer highlighting
      const rowIndex = deposit?.row_number ?? null;
      loadPreview(dataSource, page, null, rowIndex);
    },
    [loadPreview]
  );

  if (!clientId) {
    return (
      <>
        <Message severity="error" text="Client ID is required" />
      </>
    );
  }

  // Show splitter for tabs that have file viewer (Checks, Customer Deposits)
  const showFileViewer = activeTab < 2;

  const tabView = (
    <TabView activeIndex={activeTab} onTabChange={(e) => handleTabChange(e.index)}>
      <TabPanel header="Checks">
        <ChecksTab clientId={clientId} onRowSelect={handleCheckSelect} />
      </TabPanel>

      <TabPanel header="Deposits">
        <CustomerDepositsTab clientId={clientId} onRowSelect={handleDepositSelect} />
      </TabPanel>

      <TabPanel header="QB Ledger">
        <LedgerEntriesTab clientId={clientId} />
      </TabPanel>

      <TabPanel header="QB Customers">
        <QBCustomersTab clientId={clientId} />
      </TabPanel>

      <TabPanel header="QB Vendors">
        <QBVendorsTab clientId={clientId} />
      </TabPanel>
    </TabView>
  );

  // Render the file viewer based on type
  const renderFileViewer = () => {
    if (isLoadingPreview) {
      return (
        <div className="file-viewer">
          <Message severity="info" text="Loading preview..." />
        </div>
      );
    }

    if (previewError) {
      return (
        <div className="file-viewer">
          <Message severity="warn" text={previewError} />
        </div>
      );
    }

    if (!previewUrl) {
      return (
        <div className="file-viewer">
          <Message severity="info" text="Select a row to preview its source file" />
        </div>
      );
    }

    if (viewerType === 'html') {
      return (
        <div className="file-viewer">
          <iframe
            ref={iframeRef}
            src={previewUrl}
            title="Source file preview"
            style={{ width: '100%', height: '100%', border: 'none' }}
            onLoad={handleIframeLoad}
          />
        </div>
      );
    }

    // Default to PDF viewer
    return (
      <PdfViewer
        url={previewUrl}
        pageNumber={pageNumber}
        boundingBox={boundingBox}
        isLoading={isLoadingPreview}
        error={previewError}
      />
    );
  };

  return (
    <>
      {showFileViewer ? (
        <Splitter key="pdf-splitter" className="data-review-splitter">
          <SplitterPanel size={60}>
            {tabView}
          </SplitterPanel>
          <SplitterPanel size={40}>
            {renderFileViewer()}
          </SplitterPanel>
        </Splitter>
      ) : (
        <div className="data-review-full">
          {tabView}
        </div>
      )}
    </>
  );
};

export default DataReview;
